import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { EMPTY, Subject, catchError, exhaustMap, take, takeUntil, timer } from 'rxjs';

import { QRCodeComponent } from 'angularx-qrcode';
import { ProgressSpinner } from 'primeng/progressspinner';

import { APIService } from '@core/services/api.service';
import { NcpbService } from '@core/services/ncpb.service';
import { ToastService } from '@core/services/toast.service';

import { HTTPMethods } from '@core/enums';
import { ToastTypes } from '@core/enums/toast-types';
import { assets } from '@core/constants';

import { TranslateService, Translation } from '@ngx-translate/core';

type CountryItem = {
  countryCode: string;
  countryName: string;
  img: string;
};

type CreatePresentationResponse = {
  transaction_id: string;
  authorization_request_uri: string;
};

type EuWalletPresentationResponse = {
  vp_token?: Record<string, string[]>;
  issuingCountry: string;
  namespaces?: Record<string, unknown>;
  patientInfo: unknown;
};

@Component({
  selector: 'ncp-eu',
  standalone: true,
  imports: [QRCodeComponent, ProgressSpinner],
  templateUrl: './eu.component.html',
  styleUrl: './eu.component.scss',
})
export class EuComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(APIService);
  private readonly ncpbService = inject(NcpbService);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  protected translations: Translation = this.translate.instant('eu');

  protected countries: CountryItem[] = [];

  protected loading = false;
  protected walletLoading = signal(true);
  protected walletError = signal<string | null>(null);

  protected transactionId = signal('');
  protected authorizationRequestUri = signal('');

  private readonly stopPolling$ = new Subject<void>();

  private readonly verifierBaseUrl =
    'https://eudi-srv-verifier.tst.ncpeh.neha.org.cy/ui/presentations';

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(() => {
      this.translations = this.translate.instant('eu');
    });

    this.resetPatient();
    this.getCountries();
    this.createPresentation();
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
    this.stopPolling$.complete();
  }

  private get jwt(): string {
    try {
      const profile = JSON.parse(sessionStorage.getItem('profile') || '{}');

      return profile?.jwt || '';
    } catch (error) {
      console.error('Could not read profile from session storage:', error);
      return '';
    }
  }

  private get createPresentationHeaders(): HttpHeaders {
    return new HttpHeaders({
      accept: 'application/json',
      'Content-Type': 'application/json',
    });
  }

  private get pollingHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set('accept', 'application/json')
      .set('Authorization', `Bearer ${this.jwt}`);
  }

  private resetPatient(): void {
    sessionStorage.removeItem('patient');
    sessionStorage.removeItem('pToken');
    sessionStorage.removeItem('euPatient');
    sessionStorage.removeItem('euVacPresentation');

    this.apiService
      .call({
        path: '/patient/reset',
        type: HTTPMethods.PATCH,
      })
      ?.subscribe();
  }

  private getCountries(): void {
    this.loading = true;

    this.ncpbService
      .getCountries()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any[]) => {
          this.countries = (res || [])
            .map((country: any) => this.mapCountry(country))
            .filter((country: CountryItem | null): country is CountryItem => !!country);
        },
        error: (err) => {
          const message =
            err?.error?.message || err?.error?.error || err?.message || 'Unknown error';

          this.countries = [];

          this.toastService.show({
            type: ToastTypes.error,
            title: 'error',
            message,
          });
        },
      });
  }

  private mapCountry(country: any): CountryItem | null {
    const code = String(country?.countryCode || '').toLowerCase();

    if (!code) {
      return null;
    }

    const countryKey = code as keyof typeof assets.img.countries;

    const assetCountry = assets.img.countries[countryKey];

    if (!assetCountry) {
      return null;
    }

    return {
      countryCode: assetCountry.code,
      countryName: assetCountry.name,
      img: assetCountry.src,
    };
  }

  protected goToCountry = (country: CountryItem): void => {
    this.stopPolling$.next();

    this.saveSelectedCountry(country);

    this.router.navigate(['eu', country.countryCode]);
  };

  private createPresentation(): void {
    this.walletLoading.set(true);
    this.walletError.set(null);
    this.transactionId.set('');
    this.authorizationRequestUri.set('');

    const claims = [
      'health_issurance_id',
      'patient_id',
      'tax_number',
      'one_time_token',
      'e_prescription_id',
      'patient_summary_id',
      'affiliation_country',
      'institution_id',
      'family_name_1',
      'family_name_2',
      'given_name',
      'resident_address',
      'birth_date',
      'birth_place',
      'issue_date',
      'expiry_date',
      'issuing_authority',
      'issuing_country',
      'issuing_jurisdiction',
      'document_number',
      'administrative_number',
      'telephone_number',
    ];

    const payload = {
      dcql_query: {
        credentials: [
          {
            id: 'query_0',
            format: 'mso_mdoc',
            meta: {
              doctype_value: 'eu.europa.ec.eudi.hiid.1',
            },
            claims: claims.map((claim) => ({
              path: ['eu.europa.ec.eudi.hiid.1', claim],
              intent_to_retain: false,
            })),
          },
        ],
      },
      nonce: crypto.randomUUID(),
      request_uri_method: 'get',
      profile: 'openid4vp',
      authorization_request_uri: 'openid4vp://',
    };

    this.http
      .post<CreatePresentationResponse>(`${this.verifierBaseUrl}/v2`, payload, {
        headers: this.createPresentationHeaders,
      })
      .subscribe({
        next: (res) => {
          if (!res?.transaction_id || !res?.authorization_request_uri) {
            this.walletLoading.set(false);
            this.walletError.set('Invalid presentation response');
            return;
          }

          this.transactionId.set(res.transaction_id);
          this.authorizationRequestUri.set(res.authorization_request_uri);

          this.walletLoading.set(false);

          this.startPolling();
        },
        error: (err) => {
          console.error('Create presentation failed:', err);

          this.walletLoading.set(false);
          this.walletError.set('Failed to create presentation');
        },
      });
  }

  private startPolling(): void {
    timer(0, 2000)
      .pipe(
        takeUntil(this.stopPolling$),

        // 60 προσπάθειες x 2 δευτερόλεπτα = περίπου 2 λεπτά.
        take(60),

        // Δεν ξεκινά δεύτερο request όσο εκτελείται το προηγούμενο.
        exhaustMap(() => {
          const transactionId = this.transactionId();

          if (!transactionId) {
            return EMPTY;
          }

          return this.http
            .get<EuWalletPresentationResponse>(`${this.verifierBaseUrl}/${transactionId}`, {
              headers: this.pollingHeaders,
              observe: 'response',
            })
            .pipe(
              catchError((err) => {
                // Αναμενόμενο όσο δεν έχει ολοκληρωθεί η σάρωση.
                if (err?.status === 400) {
                  return EMPTY;
                }

                console.error('Wallet polling failed:', err);

                this.walletError.set('Polling failed');
                this.stopPolling$.next();

                return EMPTY;
              })
            );
        })
      )
      .subscribe({
        next: (response: HttpResponse<EuWalletPresentationResponse>) => {
          if (response.status !== 200 || !response.body) {
            return;
          }

          console.log('Presentation response:');
          console.log(response.body);

          this.handleWalletSuccess(response.body);
        },
        complete: () => {
          if (!this.walletError() && !sessionStorage.getItem('euPatient')) {
            this.walletError.set(
              'The QR code request expired. Please reload the page and try again.'
            );
          }
        },
      });
  }

  private handleWalletSuccess(response: EuWalletPresentationResponse): void {
    const countryCode = response.issuingCountry.toLowerCase() as keyof typeof assets.img.countries;

    const country = assets.img.countries[countryCode];

    /*
     * Κρατάμε ολόκληρο το wallet response για πιθανή
     * μεταγενέστερη χρήση ή debugging.
     */
    sessionStorage.setItem('euVacPresentation', JSON.stringify(response));

    /*
     * Ειδικό αντίγραφο των στοιχείων ασθενή από το wallet.
     */
    sessionStorage.setItem('euPatient', JSON.stringify(response.patientInfo));

    /*
     * Αυτό είναι το βασικό key που αναμένεται να χρησιμοποιεί
     * η υπάρχουσα ροή του PatientComponent / PatientService.
     */
    sessionStorage.setItem('patient', JSON.stringify(response.patientInfo));

    /*
     * Αποθήκευση χώρας στην ίδια μορφή με τη χειροκίνητη
     * επιλογή χώρας.
     */
    sessionStorage.setItem(
      'selectedCountry',
      JSON.stringify({
        countryCode: country.code,
        img: country.src,
        countryName: country.name,
      })
    );

    /*
     * Σταματά το polling πριν γίνει navigation.
     */
    this.stopPolling$.next();

    /*
     * Για GR:
     * /eu/gr/patient
     */
    this.router.navigate(['eu', country.code, 'patient']);
  }

  private saveSelectedCountry(country: CountryItem): void {
    sessionStorage.setItem(
      'selectedCountry',
      JSON.stringify({
        countryCode: country.countryCode,
        img: country.img,
        countryName: country.countryName,
      })
    );
  }
}
